import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import {
  Apple,
  AccessTime,
  Assignment,
  AutoAwesome,
  CalendarMonth,
  CheckCircle,
  Explore,
  GridOn,
  PeopleAlt,
  ShowChart,
  Timeline,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';
import { translatePublicSite } from '../i18n/publicSiteText';

const moduleGroups = [
  {
    eyebrow: 'Chart Systems',
    title: 'Core Astrology',
    modules: [
      { title: 'Vedic Astrology', body: 'Vedic chart analysis and supporting chart views.', icon: <AutoAwesome />, path: '/vedic-astrology-for-ios' },
      { title: 'KP Astrology', body: 'KP analysis for cusps, significators and chart study.', icon: <ShowChart />, path: '/kp-astrology-for-ios' },
      { title: 'Nadi Astrology', body: 'A dedicated Nadi analysis workflow for saved charts.', icon: <Timeline />, path: '/nadi-astrology-for-ios' },
      { title: 'BNN Astrology', body: 'Bhrigu Nandi Nadi groups, strings and chart analysis.', icon: <Explore />, path: '/bnn-astrology-for-ios' },
      { title: 'Jaimini Astrology', body: 'A dedicated Jaimini chart-analysis module.', icon: <AutoAwesome />, path: '/jaimini-astrology-for-ios' },
      { title: 'Western Astrology', body: 'Western chart views alongside the Indian systems.', icon: <ShowChart />, path: '/western-astrology-for-ios' },
    ],
  },
  {
    eyebrow: 'Specialised Analysis',
    title: 'More Ways to Study a Chart',
    modules: [
      { title: 'Tithi Pravesh', body: 'Annual Tithi Pravesh chart analysis.', icon: <CalendarMonth />, path: '/tithi-pravesh-for-ios' },
      { title: 'Lal Kitab', body: 'Lal Kitab chart, Drishti and supporting analysis.', icon: <Assignment />, path: '/lal-kitab-for-ios' },
      { title: 'Panch Pakshi', body: 'Panch Pakshi timing and activity guidance.', icon: <Timeline />, path: '/panch-pakshi-for-ios' },
      { title: 'Transit Analysis', body: 'Study current and manual planetary transits.', icon: <ShowChart />, path: '/transit-astrology-for-ios' },
      { title: 'Astro Vastu', body: 'Astrological Vastu tools available in Studio.', icon: <Explore />, path: '/astro-vastu-for-ios' },
      { title: 'Horary', body: 'Create and analyse question-based Horary charts.', icon: <AutoAwesome />, path: '/horary-chart-calculator-for-ios' },
    ],
  },
  {
    eyebrow: 'Numbers, Timing & Compatibility',
    title: 'Daily and Supporting Tools',
    modules: [
      { title: 'Vedic Numerology', body: 'Vedic number analysis from names and birth details.', icon: <GridOn />, path: '/vedic-numerology-for-ios' },
      { title: 'Lo Shu Grid', body: 'Lo Shu number grid and pattern analysis.', icon: <GridOn />, path: '/lo-shu-grid-for-ios' },
      { title: 'Watch Recommendation', body: 'Birth-date and goal-based guidance for watch type, colour, movement and fit.', icon: <AccessTime />, path: '/watch-recommendation' },
      { title: 'Daily Panchang', body: 'Daily Panchang details for your selected location.', icon: <CalendarMonth />, path: '/panchang-for-ios' },
      { title: 'Ruling Planets', body: 'A dedicated Ruling Planets calculator and view.', icon: <AutoAwesome />, path: '/ruling-planets-for-ios' },
    ],
  },
];

const workspaceTools = [
  {
    title: 'Chart Workspace',
    body: 'Create and save charts, then open the available astrology modules from one workspace.',
    icon: <ShowChart />,
  },
  {
    title: 'Client CMS',
    body: 'Organise client profiles, linked charts, relations, notes and consultation history.',
    icon: <PeopleAlt />,
  },
  {
    title: 'Planner & Tasks',
    body: 'Manage consultations, follow-ups, tasks and reminders alongside client work.',
    icon: <CalendarMonth />,
  },
  {
    title: 'Saved-Chart PDFs',
    body: 'Download supported PDF reports for saved client charts and keep report records.',
    icon: <Assignment />,
  },
];

const trackAppStoreClick = () => {
  const browserWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  browserWindow.dataLayer = browserWindow.dataLayer || [];
  browserWindow.dataLayer.push({
    event: 'app_store_click',
    app: 'kundlistar_studio',
    placement: 'homepage_hero',
  });
};

const StudioLandingPage: React.FC = () => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = (english: string) => translatePublicSite(language, english);

  return (
  <Box sx={{ bgcolor: '#f8faf8', textAlign: 'left' }}>
    <SEO
      title={text('Kundlistar Studio for iOS | Astrology for iPhone & iPad')}
      description={text('Explore Vedic, KP, Nadi, BNN, Jaimini, Western astrology, Numerology, Panchang, Horary and more in Kundlistar Studio for iPhone and iPad.')}
      path="/"
      image="/promo-banner.png"
      keywords={text('Kundlistar Studio, astrology modules iPhone, Vedic astrology iOS, KP astrology iPhone, Nadi astrology, BNN, Vedic Numerology, Lo Shu Grid')}
    />

    <Box
      sx={{
        bgcolor: 'primary.main',
        color: '#fff',
        py: { xs: 7, md: 10 },
        backgroundImage: 'radial-gradient(circle at 88% 10%, rgba(255,255,255,0.14), transparent 28%)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Chip
              label={text('Multiple astrology systems for iPhone and iPad')}
              icon={<AutoAwesome />}
              sx={{ mb: 2.5, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 800 }}
            />
            <Typography
              component="h1"
              sx={{
                color: '#fff',
                fontSize: { xs: '2.7rem', sm: '3.6rem', md: '4.5rem' },
                lineHeight: 1.02,
                letterSpacing: '-0.04em',
                fontWeight: 800,
              }}
            >
              {text('Explore more astrology with Kundlistar Studio.')}
            </Typography>
            <Typography sx={{ mt: 2.5, maxWidth: 760, color: 'rgba(255,255,255,0.88)', fontSize: { xs: 17, md: 20 }, lineHeight: 1.7 }}>
              {text('Vedic, KP, Nadi, BNN, Jaimini, Western astrology, Numerology, Panchang, Horary, timing tools and client management—together in one focused workspace.')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
              <Button
                component="a"
                href={BRAND.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackAppStoreClick}
                variant="contained"
                size="large"
                startIcon={<Apple />}
                sx={{ bgcolor: '#000', color: '#fff', fontWeight: 900, '&:hover': { bgcolor: '#252525' } }}
              >
                {text('Install the iOS App')}
              </Button>
              <Button component="a" href="#astrology-modules" variant="outlined" size="large" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.7)', fontWeight: 800 }}>
                {text('Explore All Modules')}
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ borderRadius: 4, boxShadow: '0 26px 60px rgba(0,0,0,0.18)' }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 900, letterSpacing: '0.12em' }}>
                  {text('INSIDE KUNDLISTAR STUDIO')}
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                  {[
                    'Vedic • KP • Nadi',
                    'BNN • Jaimini • Western',
                    'Numerology • Lo Shu Grid',
                    'Panchang • Ruling Planets',
                    'Horary • Transit • Astro Vastu',
                    'Lal Kitab • Panch Pakshi',
                  ].map((item) => (
                    <Paper key={item} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 2 }}>
                      <CheckCircle color="primary" />
                      <Typography sx={{ fontWeight: 800 }}>{item.split(' • ').map((part) => text(part)).join(' • ')}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box id="astrology-modules" sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: 90 }}>
      <Container maxWidth="lg">
        <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>
          {text('ALL ASTROLOGY MODULES')}
        </Typography>
        <Typography variant="h3" sx={{ mt: 1, fontWeight: 900, color: '#10251a' }}>
          {text('Choose the system that fits your analysis.')}
        </Typography>
        <Typography sx={{ mt: 1.5, maxWidth: 780, color: '#607066', fontSize: 18, lineHeight: 1.7 }}>
          {text('Studio brings its chart systems, specialised analysis, Numerology and timing tools together in one organised iOS workspace for iPhone and iPad.')}
        </Typography>

        <Stack spacing={{ xs: 5, md: 7 }} sx={{ mt: 5 }}>
          {moduleGroups.map((group) => (
            <Box key={group.title}>
              <Typography variant="caption" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.1em' }}>
                {text(group.eyebrow).toUpperCase()}
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.75, mb: 2.5, fontWeight: 900, color: '#143d28' }}>
                {text(group.title)}
              </Typography>
              <Grid container spacing={2.25}>
                {group.modules.map((module) => (
                  <Grid key={module.title} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card component={Link} to={module.path} variant="outlined" sx={{ display: 'block', height: '100%', borderRadius: 3, borderColor: '#dce7df', bgcolor: '#fff', color: 'inherit', textDecoration: 'none', transition: 'transform 160ms ease, box-shadow 160ms ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 34px rgba(16, 62, 40, 0.12)' } }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ color: 'primary.main', mb: 1.5 }}>{module.icon}</Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#143d28' }}>
                          {text(module.title)}
                        </Typography>
                        <Typography sx={{ mt: 1, color: '#607066', lineHeight: 1.65 }}>
                          {text(module.body)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>

    <Box sx={{ bgcolor: '#fff', py: { xs: 7, md: 9 }, borderBlock: '1px solid #e1e9e3' }}>
      <Container maxWidth="lg">
        <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>
          {text('CMS & WORKSPACE')}
        </Typography>
        <Typography variant="h3" sx={{ mt: 1, fontWeight: 900, color: '#10251a' }}>
          {text('Manage the work around your analysis.')}
        </Typography>
        <Grid container spacing={2.25} sx={{ mt: 3 }}>
          {workspaceTools.map((section) => (
            <Grid key={section.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, borderColor: '#dce7df' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 1.25 }}>{section.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>{text(section.title)}</Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>{text(section.body)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: '#0d3b27', color: '#fff' }}>
      <Container maxWidth="md" sx={{ textAlign: 'center' }}>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900 }}>
          {text('Take every Studio module with you.')}
        </Typography>
        <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.78)', fontSize: 18, lineHeight: 1.7 }}>
          {text('Install Kundlistar Studio for iPhone and iPad, or sign in to continue your saved workspace.')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 3.5 }}>
          <Button
            component="a"
            href={BRAND.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackAppStoreClick}
            variant="contained"
            size="large"
            startIcon={<Apple />}
            sx={{ bgcolor: '#000', color: '#fff', fontWeight: 900, '&:hover': { bgcolor: '#252525' } }}
          >
            {text('Install App')}
          </Button>
          <Button component={Link} to="/login" variant="outlined" size="large" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.7)', fontWeight: 800 }}>
            {text('Sign In')}
          </Button>
        </Stack>
      </Container>
    </Box>
  </Box>
  );
};

export default StudioLandingPage;
